/**
 * Goal Edit Modal Component
 *
 * Modal for editing existing goals.
 * Shows supporting tasks (tasks that advance this goal) with ability to remove them.
 */

import { useState, useEffect } from 'react';
import { CheckSquare, X as XIcon, Loader2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui';
import { useGoalTasks, useGoalMutations } from '@/hooks/use-goals';
import { useTasks } from '@/hooks/use-tasks';
import type { Goal, UpdateGoalInput, GoalType, GoalStatus, Task } from '@/types';

interface GoalEditModalProps {
  goal: Goal;
  isOpen: boolean;
  onClose: () => void;
  onSave: (goalId: string, input: UpdateGoalInput) => void;
  onComplete?: (goalId: string) => void;
  onDelete?: (goalId: string) => void;
  isSaving?: boolean;
  /** Called when user wants to edit a supporting task */
  onEditTask?: (task: Task) => void;
}

export function GoalEditModal({
  goal,
  isOpen,
  onClose,
  onSave,
  onComplete,
  onDelete,
  isSaving = false,
  onEditTask,
}: GoalEditModalProps) {
  const [title, setTitle] = useState(goal.title);
  const [description, setDescription] = useState(goal.description || '');
  const [goalType, setGoalType] = useState<GoalType>(goal.goalType);
  const [status, setStatus] = useState<GoalStatus>(goal.status);
  const [progress, setProgress] = useState(goal.progress);
  const [targetDate, setTargetDate] = useState(goal.targetDate || '');
  const [keyResults, setKeyResults] = useState<string[]>(
    goal.keyResults && goal.keyResults.length > 0 ? goal.keyResults : ['']
  );

  // Supporting tasks for this goal
  const { data: goalTasksData, isLoading: isLoadingGoalTasks } = useGoalTasks(goal.id);
  const { data: allTasksData } = useTasks();
  const { removeGoalTask } = useGoalMutations();
  const [isRemovingTask, setIsRemovingTask] = useState<string | null>(null);

  // Build a map of task details for display
  const taskMap = new Map<string, Task>();
  if (allTasksData?.tasks) {
    allTasksData.tasks.forEach((task) => taskMap.set(task.id, task));
  }

  // Reset form when goal changes
  useEffect(() => {
    setTitle(goal.title);
    setDescription(goal.description || '');
    setGoalType(goal.goalType);
    setStatus(goal.status);
    setProgress(goal.progress);
    setTargetDate(goal.targetDate || '');
    setKeyResults(goal.keyResults && goal.keyResults.length > 0 ? goal.keyResults : ['']);
  }, [goal]);

  // Handle removing a task's association with this goal
  const handleRemoveTask = async (taskId: string) => {
    setIsRemovingTask(taskId);
    try {
      await removeGoalTask.mutateAsync({ goalId: goal.id, taskId });
    } catch (error) {
      console.error('Failed to remove task from goal:', error);
    } finally {
      setIsRemovingTask(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    const input: UpdateGoalInput = {
      title: title.trim(),
      description: description.trim() || undefined,
      goalType,
      status,
      progress,
      targetDate: targetDate || null,
      keyResults: keyResults.filter(kr => kr.trim()).length > 0
        ? keyResults.filter(kr => kr.trim())
        : undefined,
    };

    onSave(goal.id, input);
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete(goal.id);
      onClose();
    }
  };

  const handleDelete = () => {
    if (onDelete && window.confirm('Are you sure you want to delete this goal?')) {
      onDelete(goal.id);
      onClose();
    }
  };

  const handleAddKeyResult = () => {
    setKeyResults([...keyResults, '']);
  };

  const handleKeyResultChange = (index: number, value: string) => {
    const newKeyResults = [...keyResults];
    newKeyResults[index] = value;
    setKeyResults(newKeyResults);
  };

  const handleRemoveKeyResult = (index: number) => {
    if (keyResults.length > 1) {
      setKeyResults(keyResults.filter((_, i) => i !== index));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-lg bg-white rounded-xl shadow-xl max-h-[90vh] overflow-y-auto" data-testid="goal-edit-modal">
          {/* Header */}
          <div className="sticky top-0 flex items-center justify-between px-6 py-4 border-b bg-white">
            <h2 className="text-lg font-semibold text-gray-900" data-testid="goal-edit-modal-title">Edit Goal</h2>
            <button
              data-testid="close-button"
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Title */}
            <div>
              <label htmlFor="edit-title" className="block text-sm font-medium text-gray-700 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                id="edit-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="edit-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              />
            </div>

            {/* Status and Type row */}
            <div className="grid grid-cols-2 gap-4">
              {/* Status */}
              <div>
                <label htmlFor="edit-status" className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  id="edit-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as GoalStatus)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="active">🎯 Active</option>
                  <option value="paused">⏸️ Paused</option>
                  <option value="completed">✅ Completed</option>
                  <option value="abandoned">❌ Abandoned</option>
                </select>
              </div>

              {/* Type */}
              <div>
                <label htmlFor="edit-type" className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  id="edit-type"
                  value={goalType}
                  onChange={(e) => setGoalType(e.target.value as GoalType)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="outcome">🎯 Outcome</option>
                  <option value="process">🔄 Process</option>
                  <option value="milestone">🏁 Milestone</option>
                </select>
              </div>
            </div>

            {/* Progress */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="edit-progress" className="block text-sm font-medium text-gray-700">
                  Progress
                </label>
                <span className="text-sm font-medium text-gray-900">{Math.round(progress)}%</span>
              </div>
              <input
                id="edit-progress"
                type="range"
                min="0"
                max="100"
                step="5"
                value={progress}
                onChange={(e) => setProgress(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>0%</span>
                <span>50%</span>
                <span>100%</span>
              </div>
            </div>

            {/* Target Date */}
            <div>
              <label htmlFor="edit-targetDate" className="block text-sm font-medium text-gray-700 mb-1">
                Target Date
              </label>
              <input
                id="edit-targetDate"
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Key Results */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Key Results
                </label>
                <button
                  type="button"
                  onClick={handleAddKeyResult}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  + Add
                </button>
              </div>
              <div className="space-y-2">
                {keyResults.map((kr, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={kr}
                      onChange={(e) => handleKeyResultChange(index, e.target.value)}
                      placeholder={`Key result ${index + 1}`}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    {keyResults.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveKeyResult(index)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Supporting Tasks */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <CheckSquare className="w-4 h-4 text-gray-500" />
                <label className="block text-sm font-medium text-gray-700">
                  Supporting Tasks
                </label>
                <span className="text-xs text-gray-400">
                  ({goalTasksData?.taskCount ?? 0} tasks)
                </span>
              </div>
              {isLoadingGoalTasks ? (
                <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading tasks...
                </div>
              ) : goalTasksData?.tasks && goalTasksData.tasks.length > 0 ? (
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {goalTasksData.tasks.map(({ taskId }) => {
                    const task = taskMap.get(taskId);
                    const isRemoving = isRemovingTask === taskId;
                    return (
                      <div
                        key={taskId}
                        className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 rounded-lg group"
                      >
                        <span className="text-sm text-gray-700 truncate flex-1">
                          {task?.title || taskId}
                        </span>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {onEditTask && task && (
                            <button
                              type="button"
                              onClick={() => onEditTask(task)}
                              className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                              title="Edit task"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleRemoveTask(taskId)}
                            disabled={isRemoving}
                            className="p-1 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                            title="Remove from goal"
                          >
                            {isRemoving ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <XIcon className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-400 py-2">
                  No tasks linked to this goal yet. Link tasks from the task edit dialog.
                </p>
              )}
            </div>

            {/* Quick Actions */}
            <div className="flex gap-2 pt-2 border-t">
              {goal.status !== 'completed' && onComplete && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleComplete}
                  className="text-green-600 border-green-300 hover:bg-green-50"
                >
                  ✅ Mark Complete
                </Button>
              )}
              {onDelete && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleDelete}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  🗑️ Delete
                </Button>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!title.trim() || isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

